const sensorAssignments = {
  // Nav-facing name, per the current design; permission-catalog labels below
  // keep their original wording since the underlying permission is unchanged.
  moduleTitle: "Device Assignment",
  nav: {
    sensorsManagement: "Sensor Assignment",
  },
  actions: {
    view: "View",
    create: "Create",
    update: "Update",
    delete: "Delete",
  },
  permissions: {
    core: "Core",
    sensorAssignmentManagement: "Sensor Assignment Management",
    sensorAssignments: "Sensor Assignments",
  },
  sensor: {
    types: {
      WEIGHT: "Weight",
      TEMPERATURE: "Temperature",
      DOOR_STATUS: "Door Status",
      IBUTTON: "IButton",
      PTO: "PTO",
      SEAT_BELT: "Seat Belt",
      REFRIGERATOR_STATE: "Refrigerator State",
      HEADLIGHTS: "Headlights",
      SOS: "SOS",
      RFID: "RFID",
    },
    configType: {
      NONE: "IO Number",
      SUM: "Summation Equation",
      PERCENT: "Percentage Equation",
    },
    list: {
      title: "Sensor Assignments",
      subtitle:
        "Link sensors to devices and track alert status for each device in the fleet.",
      toolbar: {
        search: "Search sensor assignments",
        searchPlaceholder: "Search...",
        add: "Assign Sensor",
      },
      table: {
        name: "Name",
        sensorType: "Sensor Type",
        type: "Type",
        minValue: "Min Value",
        maxValue: "Max Value",
        unit: "Unit",
        assignedDevice: "Assigned Device (IMEI)",
        company: "Company",
        createdDate: "Created Date",
        actionMenu: "Actions",
        empty: "No sensor assignments found.",
        error: "Failed to load sensor assignments.",
        loading: "Loading sensor assignments",
        showing: "Showing {{count}} of {{total}}",
        rowsPerPageLabel: "Rows per page",
        nextPage: "Next page",
        previousPage: "Previous page",
        pagination: "Rows per page",
        sortAscending: "Sort ascending",
        sortDescending: "Sort descending",
      },
      filters: {
        filters: "Filters",
        createdDate: "Created Date",
        createdDatePlaceholder: "Select a date range",
        clearDateRange: "Clear",
        nextMonth: "Next month",
        previousMonth: "Previous month",
      },
      actions: {
        view: "View",
        update: "Update",
        delete: "Delete",
      },
      deleteDialog: {
        title: "Delete",
        question: "Are you sure you want to delete this sensor?",
        warning: "This action is permanent and cannot be undone.",
        confirmWord: "DELETE",
        instruction: 'Type "{{word}}" to confirm deletion',
        inputLabel: "Deletion confirmation",
        confirm: "Delete",
        cancel: "Cancel",
        close: "Close delete dialog",
        successFallback: "Sensor assignment removed successfully.",
      },
    },
    detail: {
      loading: "Loading sensor settings",
      loadError: "Failed to load this sensor's settings.",
      empty: "This sensor could not be found.",
      configNotAvailable: "This sensor type's details aren't available yet.",
      backToList: "Back to sensor assignments",
      subtitle: "Details and basic information for this record",
    },
    form: {
      title: "Assign Sensor",
      next: "Next",
      back: "Back",
      save: "Save",
      cancel: "Cancel",
      typeNotAvailable: "This sensor type isn't available yet.",
      typeNotAvailableHint:
        "Support for this sensor type is coming in a later update.",
      stepOne: {
        subtitle: "Choose the company, sensor type, and target.",
        company: "Company",
        companyPlaceholder: "Select a company",
        companySearch: "Search companies",
        companyEmpty: "No companies found",
        sensorType: "Sensor Type",
        sensorTypePlaceholder: "Select a sensor type",
        configurationType: "Configuration Type",
        configurationTypeNew: "New configuration",
        configurationTypeCopy: "Copy existing configuration",
        sourceCompany: "Source Company",
        sourceCompanyPlaceholder: "Select a company",
        copyFromDevice: "Copy configuration from device",
        copyFromDevicePlaceholder: "IMEI Number",
        copyPickSensorTypeFirst: "Choose a sensor type first.",
        copyNoMatchingSensor: "This device has no {{sensorType}} sensor configured.",
        copyConfigUnavailable: "This device's configuration can't be copied right now.",
        copyDeviceRequired: "Choose a device to copy the configuration from.",
        targetDeviceSearch: "Search by IMEI",
        targetDeviceEmpty: "No devices found",
        targetSection: "Target",
        targetType: "Target Type",
        targetTypeAll: "All",
        targetTypeBranch: "Branches",
        targetTypeDevices: "One or more devices",
        targetDestination: "Target",
        targetTypeAllCount: "All ({{count}})",
        targetBranchPlaceholder: "Select a branch",
        targetBranchEmpty: "No branches found",
        targetBranchRequired: "Choose a branch.",
        targetDevicesPlaceholder: "Select devices",
        targetDevicesRequired: "Choose at least one device.",
        targetDeviceAlreadyAssigned:
          "This sensor has already been assigned to the selected device.",
        removeTargetDevice: "Remove {{imei}}",
        targetResolveFailed: "Couldn't load the devices for this target. Please try again.",
        targetNoDevicesFound: "No devices found for this target.",
      },
      weight: {
        title: "Sensor Settings - Weight",
        subtitle: "Add one or more IO readings, then combine them if needed.",
        unit: "Unit",
        addIo: "I/O",
        addFormula: "Formula",
        addSum: "Sum formula",
        addPercent: "Percentage formula",
        ioSectionLabel: "Input/Output (I/O)",
        io: "IO Number",
        ioPlaceholder: "e.g. 9",
        label: "Label",
        minWeight: "Min Weight (KGs)",
        maxWeight: "Max Weight (KGs)",
        millivolt: "Millivolt",
        kilograms: "Kilograms",
        percentage: "Weight Percentage",
        addCalibrationPoint: "Add",
        removeCalibrationPoint: "Remove calibration point {{number}}",
        calibrationEcuHint:
          "Leave the calibration table empty for weight sensors calculated by the Electronic Control Unit (ECU).",
        calibrationRangeHint:
          "The calibration kilogram value must start at the min weight and end at the max weight.",
        removeRow: "Remove",
        editRow: "Edit",
        ioDialogTitle: "IO Configuration",
        ioDialogSubtitle: "IO details",
        ioDialogMainTab: "Main",
        ioDialogCalibrationTab: "Calibration",
        formulaDialogTitle: "Formula Settings",
        formulaDialogSubtitle: "Formula details",
        selectIoLabel: "Choose I/O",
        dialogClose: "Close",
        dialogSave: "Save",
        formulaCard: {
          editFormula: "Edit",
          deleteFormula: "Delete",
          table: {
            name: "Name",
            type: "Formula Type",
            percentage: "Percentage",
            percentageChart: "Percentage Chart",
          },
        },
        formulaClearedOnRowRemoved:
          "The formula was cleared because a row it combined was removed. Add a new one if you still need it.",
        clearFormulaDialog: {
          title: "Clear the current formula?",
          description:
            "This will clear the current {{formulaType}}. Its I/O readings will remain, but as independent, uncombined readings. This can't be undone.",
          confirm: "Clear formula",
          close: "Close",
        },
        switchFormulaTypeDialog: {
          title: "Switch formula type?",
          description:
            "Switching will erase the data you've entered for this formula. This can't be undone.",
          confirm: "Switch",
          close: "Close",
        },
        table: {
          label: "Label",
          io: "IO Number",
          minWeight: "Min Weight (KGs)",
          maxWeight: "Max Weight (KGs)",
          calibration: "Calibration",
          unit: "Unit",
          empty: "No IO readings added yet.",
          ecu: "ECU",
          calibrationPoints: "{{count}} point",
          calibrationPoints_other: "{{count}} points",
        },
        validation: {
          required: "This field is required.",
          ioRange: "Enter a whole number between 0 and 65535.",
          ioDuplicate: "This IO number is already used in this configuration.",
          maxWeight: "Max weight must be greater than min weight.",
          labelTooLong: "Must be {{count}} characters or fewer.",
          calibrationMinPoints: "Add at least one more calibration point, or remove it.",
          calibrationDistinctMv: "Calibration millivolt values must be distinct.",
          atLeastOneIo: "Add at least one IO reading before saving.",
          percentageTotal:
            "The total Weight Percentage of all Weight Sensors must equal 100%.",
        },
        ioConflict:
          "This sensor has already been assigned to the selected device (IO {{ios}}).",
        saveSuccess: "Sensor assigned successfully.",
        updateSuccess: "Sensor updated successfully.",
        saveFailed: "Failed to save the sensor configuration.",
      },
      temperature: {
        title: "Sensor Settings - Temperature",
        subtitle: "Add one or more probes.",
        label: "Label",
        probeType: "Sensor Type",
        temperatureIo: "Temperature IO Number",
        temperatureUnit: "Temperature Unit",
        humidityIo: "Humidity IO Number",
        humidityUnit: "Humidity Unit",
        batteryIo: "Battery IO Number",
        batteryUnit: "Battery Unit",
        dallasIo: "DALLAS ID IO Number",
        beaconModel: "Multiplier",
        beaconModels: {
          TZONE_BT04: "0.1 (Tzone BLE BT04)",
          MINEW_MST01: "1 (Minew MST01)",
        },
        table: {
          probeType: "Sensor Type",
          label: "Sensor Name",
          temperatureIo: "Temperature IO",
          humidityIo: "Humidity IO",
          batteryIo: "Battery IO",
          dallasIo: "DALLAS IO",
          empty: "No probes added yet.",
        },
        validation: {
          atLeastOneIo: "Add at least one probe before saving.",
        },
      },
      simpleIo: {
        title: "Sensor Settings - {{type}}",
        subtitle: "Add one or more IO readings.",
      },
      rfid: {
        title: "Sensor Settings - RFID",
        subtitle: "Add one or more IO readings with their tag verification length.",
        tagLength: "Tag Verification Length",
        tagLengthPlaceholder: "e.g. 2",
        validation: {
          tagLengthRange: "Enter a whole number between 1 and 64.",
        },
      },
      valueMap: {
        title: "Sensor Settings - {{type}}",
        rowLabel: "Name",
        value: "Value",
        labelEn: "Label (English)",
        labelAr: "Label (Arabic)",
        table: {
          values: "Values",
          empty: "No readings added yet.",
        },
        validation: {
          valuesDuplicate: "The two values must be different.",
        },
      },
    },
  },
};

export default sensorAssignments;
